import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { apiFetch } from "@/api/client";
import {
  deactivateEmployee,
  getEmployee,
  listAnalysisTemplates,
  listListAnalyses,
  listMeetingsQS,
  restoreEmployee,
} from "@/api/lookups";
import type { EmployeeResponse, MeetingSummary, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/table/DataTable";
import {
  makeMeetingSelectColumn,
  useMeetingSelection,
} from "@/components/table/meetingSelection";
import { EmployeeFormDialog } from "@/components/registries/EmployeeFormDialog";
import { AnalysisHistorySection } from "@/components/employee-card/AnalysisHistorySection";
import { ManagerAnalysisDialog } from "@/components/employee-card/ManagerAnalysisDialog";
import { ListAnalysisDialog } from "@/components/analysis/ListAnalysisDialog";
import { ListAnalysisHistory } from "@/components/analysis/ListAnalysisHistory";
import { useCan } from "@/hooks/useCan";
import { useFolders } from "@/hooks/useFolders";
import { useMeetingColumns } from "@/pages/registries/useMeetingColumns";

export interface EmployeeCardViewProps {
  employeeId: UUID;
}

/** Hard cap on the card's meetings list — no paginator in v1 (TM-035ae774). */
const MEETINGS_LIMIT = 50;

/**
 * Employee card (TM-035ae774).
 *
 * Header mirrors the meeting-card's `CardHeaderBlock` style: name + an
 * «неактивен» badge for soft-deleted rows, with «Изменить» (opens the
 * existing `EmployeeFormDialog`) and a «Деактивировать»/«Восстановить»
 * toggle gated on `employee_deactivate`. Below the fields, a «Встречи (N)»
 * section renders the shared meetings table filtered by `participant_id`.
 */
export function EmployeeCardView({ employeeId }: EmployeeCardViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = useCan("employee_crud");
  const canDeactivate = useCan("employee_deactivate");
  const canAnalyze = useCan("manager_analysis_create");
  const canListAnalyze = useCan("list_analysis_create");
  const [editing, setEditing] = useState(false);
  const selection = useMeetingSelection();
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const { folders } = useFolders();

  const employee = useQuery({
    queryKey: ["employee", employeeId],
    queryFn: () => getEmployee(employeeId),
  });

  const meetings = useQuery({
    queryKey: ["meetings", "by-participant", employeeId],
    queryFn: () =>
      apiFetch<MeetingSummary[]>(
        `/api/meetings${listMeetingsQS({
          participant_id: employeeId,
          limit: MEETINGS_LIMIT,
        })}`,
        { method: "GET" },
      ),
  });

  // Feature-gate: пробуем list-templates. 404 → роутер не смонтирован
  // (FEATURE_MANAGER_ANALYSIS=false) → скрываем UI; 200 → показываем.
  // retry=false чтобы не дублировать 404.
  const featureProbeQ = useQuery({
    queryKey: ["analysis-templates", { activeOnly: true, probe: true }],
    queryFn: () => listAnalysisTemplates({ includeInactive: false }),
    enabled: canAnalyze,
    retry: false,
    staleTime: 60_000,
  });
  const featureEnabled =
    canAnalyze && featureProbeQ.isSuccess && Array.isArray(featureProbeQ.data);

  // Independent feature-probe for the API-based list-analysis (TM-4c1fb82b):
  // 404 → the /api/list_analyses router isn't mounted → hide the new UI.
  const listProbeQ = useQuery({
    queryKey: ["list-analyses", "probe", { employeeId }],
    queryFn: () => listListAnalyses({ employee_id: employeeId, limit: 1 }),
    enabled: canListAnalyze,
    retry: false,
    staleTime: 60_000,
  });
  const listFeatureEnabled =
    canListAnalyze && listProbeQ.isSuccess && Array.isArray(listProbeQ.data);

  const baseColumns = useMeetingColumns({ hideParticipants: true });
  const columns = useMemo<ColumnDef<MeetingSummary, unknown>[]>(() => {
    if (!featureEnabled && !listFeatureEnabled) return baseColumns;
    return [makeMeetingSelectColumn(selection), ...baseColumns];
  }, [baseColumns, featureEnabled, listFeatureEnabled, selection]);

  const toggleMut = useMutation({
    mutationFn: (emp: EmployeeResponse) =>
      emp.is_active ? deactivateEmployee(emp.id) : restoreEmployee(emp.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["employee", employeeId],
      });
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  if (employee.isLoading) {
    return <p>{t("employees.card.loading")}</p>;
  }
  if (employee.isError || employee.data === undefined) {
    return (
      <p role="alert" className="text-destructive">
        {t("employees.card.loadError")}
      </p>
    );
  }

  const emp = employee.data;
  const folderNameById = new Map(folders.map((f) => [f.id, f.name]));

  function handleToggle(): void {
    const msg = emp.is_active
      ? t("employees.confirm.deactivate", { name: emp.full_name })
      : t("employees.confirm.restore", { name: emp.full_name });
    if (typeof window !== "undefined" && !window.confirm(msg)) return;
    toggleMut.mutate(emp);
  }

  const meetingRows = meetings.data ?? [];

  return (
    <div className="space-y-4" data-testid="employee-card">
      <Link
        to="/employees"
        className="text-sm text-primary underline-offset-4 hover:underline"
        data-testid="employee-card-back"
      >
        {t("employees.card.backToList")}
      </Link>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle
            className="flex items-center gap-2"
            data-testid="employee-card-title"
          >
            <span>{emp.full_name}</span>
            {!emp.is_active ? (
              <span
                className="inline-flex items-center rounded-full border border-muted-foreground/30 bg-muted/30 px-2 py-0.5 text-xs font-medium text-muted-foreground"
                data-testid="employee-card-inactive-badge"
              >
                {t("employees.card.inactive")}
              </span>
            ) : null}
          </CardTitle>
          <div className="flex items-center gap-2">
            {canEdit ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                data-testid="employee-card-edit"
              >
                {t("employees.card.edit")}
              </Button>
            ) : null}
            {canDeactivate ? (
              <Button
                type="button"
                variant={emp.is_active ? "outline" : "default"}
                size="sm"
                disabled={toggleMut.isPending}
                onClick={handleToggle}
                data-testid="employee-card-toggle-active"
              >
                {emp.is_active
                  ? t("employees.card.deactivate")
                  : t("employees.card.restore")}
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <Field
              label={t("employees.fields.position")}
              value={emp.position}
            />
            <Field
              label={t("employees.fields.department")}
              value={emp.department}
            />
            <Field
              label={t("employees.fields.meetingCount")}
              value={String(emp.meeting_count)}
              testId="employee-card-meeting-count"
            />
            <Field
              label={t("employees.fields.analysisCount")}
              value={String(emp.analysis_count)}
              testId="employee-card-analysis-count"
            />
            <Field
              label={t("employees.fields.aliases")}
              value={emp.aliases.length > 0 ? emp.aliases.join(", ") : null}
            />
            <Field
              label={t("employees.fields.folders")}
              value={
                emp.folder_ids.length > 0
                  ? emp.folder_ids
                      .map((id) => folderNameById.get(id) ?? id)
                      .join(", ")
                  : null
              }
            />
            <Field
              label={t("employees.columns.account")}
              value={emp.linked_user?.email ?? null}
            />
            {Object.entries(emp.custom_field_values).map(([k, v]) => (
              <Field
                key={k}
                label={k}
                value={
                  v === null || v === undefined || v === "" ? null : String(v)
                }
              />
            ))}
          </dl>
        </CardContent>
      </Card>

      <section className="space-y-2" data-testid="employee-card-meetings">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {t("employees.card.meetings", { count: meetingRows.length })}
          </h2>
          <div className="flex items-center gap-2">
            {featureEnabled ? (
              <Button
                type="button"
                size="sm"
                disabled={selection.count === 0}
                onClick={() => setAnalysisDialogOpen(true)}
                data-testid="employee-card-analyze-button"
              >
                {selection.count > 0
                  ? t("managerAnalysis.section.analyzeButton", {
                      count: selection.count,
                    })
                  : t("managerAnalysis.section.analyzeButtonZero")}
              </Button>
            ) : null}
            {listFeatureEnabled ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={selection.count === 0}
                onClick={() => setListDialogOpen(true)}
                data-testid="employee-card-list-analyze-button"
              >
                {selection.count > 0
                  ? t("listAnalysis.section.analyzeApi", {
                      count: selection.count,
                    })
                  : t("listAnalysis.section.analyzeZero")}
              </Button>
            ) : null}
          </div>
        </div>
        <DataTable
          data={meetingRows}
          columns={columns}
          testId="employee-card-meetings-table"
          isLoading={meetings.isLoading}
          emptyMessage={t("employees.card.noMeetings")}
          onRowClick={(row) =>
            void navigate({
              to: "/meetings/$meetingId",
              params: { meetingId: row.id },
            })
          }
        />
      </section>

      {featureEnabled ? (
        <AnalysisHistorySection employeeId={employeeId} />
      ) : null}

      {listFeatureEnabled ? (
        <ListAnalysisHistory mode="manager" employeeId={employeeId} />
      ) : null}

      {featureEnabled ? (
        <ManagerAnalysisDialog
          open={analysisDialogOpen}
          employeeId={employeeId}
          selectedMeetingIds={Array.from(selection.selectedIds)}
          onClose={() => setAnalysisDialogOpen(false)}
          onSubmitted={() => selection.clear()}
        />
      ) : null}

      {listFeatureEnabled ? (
        <ListAnalysisDialog
          open={listDialogOpen}
          mode="manager"
          employeeId={employeeId}
          selectedMeetingIds={Array.from(selection.selectedIds)}
          onClose={() => setListDialogOpen(false)}
          onSubmitted={() => selection.clear()}
        />
      ) : null}

      <EmployeeFormDialog
        open={editing}
        existing={emp}
        onClose={() => setEditing(false)}
      />
    </div>
  );
}

/** One label/value pair in the card's definition list. */
function Field({
  label,
  value,
  testId,
}: {
  label: string;
  value: string | null;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid={testId}>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5">{value ?? t("common.dash")}</dd>
    </div>
  );
}
